import * as React from 'react';
import { Navigate } from 'react-router-dom';
import useSWR from 'swr';

import { getMe, logout as doLogout, type MeResponse } from '../app/auth/models';
import createContext from './create-context';
import { useTranslation } from './translation-context';
import { SpinnerBlock } from 'components/Spinner';
import { posthog } from '../utils/posthog.client';
import { ErrorState } from 'components/error-state/ErrorState';
import { getUserFacingError } from '@utils/get-display-error';

export const INTERNAL_ROLES = ['InternalAdmin'];

interface ProviderValue {
  me: MeResponse;
  logout: () => void;
  refreshMe: () => void;
}

const [useContext, ReactProvider, ReactConsumer] = createContext<ProviderValue>();

interface AuthProviderProps {
  children?: React.ReactNode;
  fallbackLogin: string;
}

export const AuthProvider: React.FC<AuthProviderProps> = (props) => {
  const { children, fallbackLogin } = props;
  const {
    data: me,
    error,
    isLoading,
    mutate,
  } = useSWR('auth/me', getMe, {
    revalidateOnFocus: false,
    revalidateOnReconnect: true,
  });
  const { setLanguage } = useTranslation();

  const refreshMe = React.useCallback(() => {
    mutate();
  }, [mutate]);

  const logout = React.useCallback(() => {
    posthog.reset();
    doLogout();
  }, []);

  React.useEffect(() => {
    if (me) {
      setLanguage(me.language);
      posthog.identify(me.id, {
        email: me.email,
        name: me.name,
        is_internal: me.isInternal,
        role: me.role,
        carrier_id: me.carrier?.id ?? null,
        customer_ids: me.customers.map((c) => c.customer.id),
      });
    }
  }, [me, setLanguage]);

  if (!me && isLoading) {
    return <SpinnerBlock message="Loading..." />;
  }

  // A failed `auth/me` with an unauthorized (401), permission (403) or
  // not-found (404 — the session points at a deleted user) error just means the
  // user isn't logged in — fall through to the login redirect below.
  // Only surface genuine errors (network, server, …) as an error page.
  if (!me && error) {
    const kind = getUserFacingError(error).kind;
    if (kind !== 'unauthorized' && kind !== 'permission' && kind !== 'not-found') {
      return <ErrorState error={error} onRetry={() => mutate()} variant="page" />;
    }
  }

  if (!me) {
    return <Navigate to={fallbackLogin} replace />;
  } else {
    return <ReactProvider value={{ me, logout, refreshMe }}>{children}</ReactProvider>;
  }
};

export const useAuth = useContext;
export const AuthConsumer = ReactConsumer;
